import React, { useEffect } from "react";
import ProgressBar from "../common/ProgressBar.jsx";
import Sparkline from "../common/Sparkline.jsx";
import InlineBanner from "../common/InlineBanner.jsx";
import BButton from "../BButton.mjs";
import { useConfirm } from "../../context/ConfirmContext";
import { useUploadManager, useUploadSession } from "../../context/UploadManagerContext";
import { fmtBytes } from "../../utils/format";
import { fmtSpeed, fmtRate, fmtEta, fmtDuration } from "../../utils/uploadStats";
import "../../css/UploadProgressPanel.css";

const isActive = (phase) => phase === "uploading" || phase === "finalizing";
const displayPath = (f) => (f.folderPath ? `${f.folderPath}/${f.name}` : f.name);
const UPLOADED_RENDER_CAP = 300; // cap rendered rows so a 1000+-file import stays snappy

function statusInfo(s) {
    switch (s.phase) {
        case "uploading":
            return { label: "Uploading", tone: "accent" };
        case "finalizing":
            return { label: "Processing", tone: "accent" };
        case "done":
            return s.result.failed > 0
                ? { label: "Completed with errors", tone: "warning" }
                : { label: "Completed", tone: "success" };
        case "error":
            // `result.succeeded` is the count we can map to specific files; a
            // stalled job's successes are only known by the server's live count
            // (succeededLive), so consider both before calling it an outright fail.
            return {
                label: s.result.succeeded > 0 || s.succeededLive > 0 ? "Finished with failures" : "Failed",
                tone: "danger",
            };
        case "canceled":
            return { label: "Cancelled", tone: "muted" };
        default:
            return { label: "Pending", tone: "muted" };
    }
}

const toBarVariant = (tone) => (tone === "muted" ? "accent" : tone);

const Metric = ({ label, value }) => (
    <div className="up-metric">
        <span className="up-metric-value">{value}</span>
        <span className="up-metric-label">{label}</span>
    </div>
);

/**
 * UploadProgressPanel — the rich, live progress dashboard for ONE upload
 * session. Shared by the Import wizard, the quick Upload modal, and the
 * tray-launched details modal. While mounted it marks the session "presented"
 * so the floating tray doesn't also show it (no double display).
 *
 * Props:
 *   sessionId — the session to render
 *   onClose   — host close handler (used for Minimize / Done). Optional.
 */
const UploadProgressPanel = ({ sessionId, onClose }) => {
    const confirm = useConfirm();
    const { retrySession, cancelSession, dismissSession, present, unpresent } = useUploadManager();
    const session = useUploadSession(sessionId);

    useEffect(() => {
        present(sessionId);
        return () => unpresent(sessionId);
    }, [sessionId, present, unpresent]);

    if (!session) {
        return (
            <div className="up-panel up-panel--empty">
                <p className="up-empty">This upload is no longer available.</p>
                {onClose && (
                    <BButton className="btn-primary" onClick={onClose}>
                        Close
                    </BButton>
                )}
            </div>
        );
    }

    const s = session;
    const active = isActive(s.phase);
    const { label, tone } = statusInfo(s);
    const pct = Math.round((s.progress || 0) * 100);
    const speedText = s.rateUnit === "files" ? fmtRate(s.speed, "files") : fmtSpeed(s.speed);
    const transferred =
        s.phase === "finalizing"
            ? `${s.processed}/${s.processedTotal || s.filesTotal}`
            : `${fmtBytes(s.bytesLoaded)} / ${fmtBytes(s.bytesTotal)}`;

    const handleCancel = async () => {
        const ok = await confirm({
            title: "Stop this upload?",
            message:
                s.kind === "import"
                    ? "Files still uploading won't be imported. Files already uploaded this run won't be registered either."
                    : "Files still uploading will be stopped. Files already uploaded are kept.",
            confirmText: "Stop upload",
            danger: true,
        });
        if (ok) cancelSession(s.id);
    };

    const handleDismiss = () => {
        dismissSession(s.id);
        onClose?.();
    };

    const uploadedFiles = s.uploadedFiles || [];

    return (
        <div className="up-panel">
            {s.sourceName && (
                <div className="up-source" title={s.sourceName}>
                    <span className="up-source-icon" aria-hidden="true">
                        {s.kind === "import" ? "📦" : "📤"}
                    </span>
                    <span className="up-source-name">{s.sourceName}</span>
                </div>
            )}
            <div className="up-hero">
                <div className="up-hero-stat">
                    <div className="up-pct">{pct}%</div>
                    <div className={`up-status up-status--${tone}`}>{label}</div>
                </div>
                <div className="up-graph">
                    <div className="up-graph-head">
                        <span>{s.rateUnit === "files" ? "Processing rate" : "Upload speed"}</span>
                        <strong>{speedText}</strong>
                    </div>
                    <Sparkline
                        data={s.samples}
                        variant={tone === "muted" ? "accent" : tone}
                        height={56}
                        ariaLabel="Upload throughput over time"
                    />
                </div>
            </div>

            <div className="up-metrics">
                <Metric label="Speed" value={speedText} />
                <Metric label="Time left" value={active ? fmtEta(s.etaMs) : "—"} />
                <Metric label="Elapsed" value={fmtDuration(s.elapsedMs)} />
                <Metric label={s.phase === "finalizing" ? "Processed" : "Transferred"} value={transferred} />
            </div>

            <ProgressBar value={pct} max={100} variant={toBarVariant(tone)} />
            <p className="up-sub">
                {s.phase === "finalizing"
                    ? `${s.processed}/${s.processedTotal || s.filesTotal} processed · ${s.succeededLive} ok · ${s.failedLive} failed`
                    : `${s.filesDone}/${s.filesTotal} files · ${transferred}`}
            </p>

            {s.phase === "uploading" && s.activeTransfers.length > 0 && (
                <div className="up-transfers">
                    <div className="up-transfers-head">
                        Transferring {s.activeTransfers.length} file{s.activeTransfers.length === 1 ? "" : "s"} in parallel
                    </div>
                    {s.activeTransfers.slice(0, 8).map((t) => (
                        <div className="up-transfer" key={t.key}>
                            <span className="up-transfer-name" title={t.name}>
                                {t.name}
                            </span>
                            <div className="up-transfer-track">
                                <div className="up-transfer-fill" style={{ width: `${t.pct}%` }} />
                            </div>
                            <span className="up-transfer-pct">{t.pct}%</span>
                        </div>
                    ))}
                </div>
            )}

            {s.kind === "import" && s.phase === "error" && (
                <InlineBanner variant="danger" className="up-banner">
                    The import didn’t finish
                    {s.succeededLive > 0
                        ? ` — about ${s.succeededLive} of ${s.filesTotal} file${s.filesTotal === 1 ? "" : "s"} imported before it stopped`
                        : ""}
                    .
                    {s.retryableCount > 0
                        ? ` You can retry the ${s.retryableCount} file${s.retryableCount === 1 ? "" : "s"} below that are safe to re-import.`
                        : ""}
                </InlineBanner>
            )}
            {s.warnings.map((w, i) => (
                <InlineBanner key={i} variant="warning" className="up-banner">
                    {w}
                </InlineBanner>
            ))}

            {!active && uploadedFiles.length > 0 && (
                <div className="up-uploaded">
                    <h4>{s.kind === "import" ? "Imported" : "Uploaded"} ({uploadedFiles.length})</h4>
                    <ul>
                        {uploadedFiles.slice(0, UPLOADED_RENDER_CAP).map((f, i) => (
                            <li key={i}>
                                <span className="up-uploaded-name" title={displayPath(f)}>
                                    {displayPath(f)}
                                </span>
                                {f.isAudio && f.fileId && (
                                    <a className="up-text-link" href={`/split/${f.fileId}`}>
                                        Split ✂
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                    {uploadedFiles.length > UPLOADED_RENDER_CAP && (
                        <p className="up-uploaded-more">
                            + {uploadedFiles.length - UPLOADED_RENDER_CAP} more file
                            {uploadedFiles.length - UPLOADED_RENDER_CAP === 1 ? "" : "s"}
                        </p>
                    )}
                </div>
            )}

            {s.failures.length > 0 && (
                <details className="up-failures">
                    <summary>Failed ({s.failures.length})</summary>
                    <ul>
                        {s.failures.map((f, i) => (
                            <li key={i}>
                                {displayPath(f)} — {f.error}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            <div className="up-actions">
                {active ? (
                    <>
                        {s.canCancel && (
                            <BButton className="btn-danger" onClick={handleCancel}>
                                Stop
                            </BButton>
                        )}
                        {onClose && (
                            <BButton className="btn-primary" onClick={onClose}>
                                Minimize
                            </BButton>
                        )}
                    </>
                ) : (
                    <>
                        {s.retryableCount > 0 && (
                            <BButton onClick={() => retrySession(s.id)}>Retry failed ({s.retryableCount})</BButton>
                        )}
                        <BButton className="btn-secondary" onClick={handleDismiss}>
                            Dismiss
                        </BButton>
                        {onClose && (
                            <BButton className="btn-primary" onClick={onClose}>
                                Done
                            </BButton>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default UploadProgressPanel;
